import { Button, CabecalhoDePagina, CampoDeTexto, toast } from "@base/ui";
import { Building2, LoaderCircle, MapPin, Stethoscope, UserRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { useClienteSelecionado } from "../application/clientes.contexto";
import { useAtualizarCliente } from "../application/use-clientes";

const ESPECIALIDADES_SUGERIDAS = [
  "Clínica geral",
  "Ortodontia",
  "Implantodontia",
  "Harmonização facial",
  "Odontopediatria",
  "Endodontia",
  "Periodontia",
  "Estética",
];

/** Dados cadastrais do cliente selecionado: nome, especialidade, cidade e responsável. */
export function PaginaDeDadosDoProjeto({
  clienteId,
  cliente,
}: {
  readonly clienteId: string;
  readonly cliente: string;
}) {
  const { clientes } = useClienteSelecionado();
  const atual = clientes.find((item) => item.id === clienteId);

  return (
    <div className="flex flex-col gap-secao">
      <CabecalhoDePagina
        rotulo={`${cliente} · Configurações`}
        titulo="Dados do projeto"
        descricao="Informações básicas desta clínica no painel. O nome aparece no seletor da barra lateral."
      />
      {atual ? (
        <div className="max-w-xl rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70 md:p-8">
          <FormularioDeDadosDoProjeto
            key={`${atual.id}|${atual.atualizadoEm}`}
            clienteId={atual.id}
            inicial={{
              nome: atual.nome,
              especialidade: atual.especialidade,
              cidade: atual.cidade,
              responsavelNaMonte: atual.responsavelNaMonte,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function FormularioDeDadosDoProjeto({
  clienteId,
  inicial,
}: {
  readonly clienteId: string;
  readonly inicial: {
    nome: string;
    especialidade: string | null;
    cidade: string | null;
    responsavelNaMonte: string | null;
  };
}) {
  const [nome, setNome] = useState(inicial.nome);
  const [especialidade, setEspecialidade] = useState(inicial.especialidade ?? "");
  const [cidade, setCidade] = useState(inicial.cidade ?? "");
  const [responsavel, setResponsavel] = useState(inicial.responsavelNaMonte ?? "");
  const salvar = useAtualizarCliente();
  const campos = salvar.error instanceof ErroDaApi ? salvar.error.campos : {};

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate(
      {
        id: clienteId,
        dados: {
          nome: nome.trim(),
          especialidade: especialidade.trim() || null,
          cidade: cidade.trim() || null,
          responsavelNaMonte: responsavel.trim() || null,
        },
      },
      { onSuccess: () => toast.success("Dados do projeto salvos") },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-5">
      <CampoDeTexto
        rotulo="Nome da clínica"
        value={nome}
        onChange={(evento) => setNome(evento.target.value)}
        icone={<Building2 aria-hidden="true" />}
        erros={campos.nome}
        required
      />
      <CampoDeTexto
        rotulo="Especialidade"
        value={especialidade}
        onChange={(evento) => setEspecialidade(evento.target.value)}
        list="especialidades-sugeridas"
        placeholder="Ex.: Ortodontia, Clínica geral"
        icone={<Stethoscope aria-hidden="true" />}
        erros={campos.especialidade}
      />
      <datalist id="especialidades-sugeridas">
        {ESPECIALIDADES_SUGERIDAS.map((sugestao) => (
          <option key={sugestao} value={sugestao} />
        ))}
      </datalist>
      <CampoDeTexto
        rotulo="Cidade"
        value={cidade}
        onChange={(evento) => setCidade(evento.target.value)}
        autoComplete="address-level2"
        placeholder="Ex.: Porto Alegre"
        icone={<MapPin aria-hidden="true" />}
        erros={campos.cidade}
      />
      <CampoDeTexto
        rotulo="Responsável na Monte"
        value={responsavel}
        onChange={(evento) => setResponsavel(evento.target.value)}
        placeholder="Quem conduz este projeto"
        icone={<UserRound aria-hidden="true" />}
        erros={campos.responsavelNaMonte}
      />

      {salvar.isError && Object.keys(campos).length === 0 ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDoErro(salvar.error)}
        </p>
      ) : null}

      <Button type="submit" disabled={salvar.isPending || nome.trim().length < 2}>
        {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
        Salvar dados
      </Button>
    </form>
  );
}
